import { useMemo } from 'react';

import { Card, Form } from '@arco-design/web-react';

import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { resolveMsPreviewSpec } from '../headless/previewSpec';
import { MS_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'alias',
  'workspace',
  'description',
  'strategyName',
  'strategyDisplayName',
  'strategyProvider',
  'providerPricing',
  'allowedClientCIDRs',
  'allowedClientIPs',
  'billingTotalTokens',
  'inputTokens',
  'outputTokens',
  'requests',
  'inputTokensValue',
  'inputTokensWindowSeconds',
  'outputTokensValue',
  'outputTokensWindowSeconds',
  'switches',
  'switches.showRequests',
  'switches.showInputTokens',
  'switches.showOutputTokens',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '-';
const ALL_MODULE_KEYS = MS_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]) {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

/** 模型订阅 ResourcePreview：仅展示 skuResourceModules 勾选的模块 */
export function MsResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('ModelSubscription');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const skuResourceModules = form?.getFieldValue('skuResourceModules') as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'ModelSubscription', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    cluster: ctx?.cluster,
    sku: {
      ...ctx?.sku,
      enabledModules: previewEnabledModules,
    },
  };
  const resolvedSpec = resolveMsPreviewSpec(form, spec, previewCtx);
  const profile = plugin.pipeline.projectionProfile;
  const flatItems =
    descriptors ?? (isSkuCapableProfile(profile) ? profile.projections.buildDisplayDescriptors(resolvedSpec, previewCtx) : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const visibleModules = MS_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => {
        const items = grouped.get(mod.key) ?? [{ id: `${mod.key}-empty`, group: mod.key, label: '-', value: PREVIEW_EMPTY }];

        return (
          <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
            <DescriptorDescriptions items={items} column={2} />
          </Card>
        );
      })}
    </>
  );
}
